<template>
  <Dialog
    v-model:visible="isOpen"
    modal
    :dismissable-mask="true"
    :draggable="false"
    :closable="false"
    position="top"
    class="command-palette-dialog"
    :pt="{
      root: { class: 'command-palette-root' },
      mask: { class: 'command-palette-mask' },
    }"
    @hide="onClose"
  >
    <template #header>
      <div class="w-full">
        <IconField class="w-full">
          <InputIcon>
            <i class="pi pi-search" />
          </InputIcon>
          <InputText
            ref="searchInputRef"
            v-model="searchQuery"
            placeholder="Pages, members, SubKrewes"
            aria-label="Search pages, members and SubKrewes"
            class="w-full command-palette-input"
            @keydown="onKeyDown"
          />
        </IconField>
      </div>
    </template>

    <div class="command-palette-results">
      <!-- Loading state -->
      <KbSkeletonRows
        v-if="isLoading"
        class="px-4"
        :rows="4"
        label="Loading"
      />

      <!-- No results, once the member search has finished too -->
      <div
        v-else-if="isSearchable && !hasResults && !showMemberStatus"
        class="p-4 text-center text-[13px] text-kb-muted"
      >
        Nothing matches "{{ searchQuery }}".
      </div>

      <!-- Results -->
      <div v-else-if="hasResults || showMemberStatus">
        <!-- Pages/Navigation section -->
        <div
          v-if="filteredNavItems.length > 0"
          class="mb-2"
        >
          <div class="kb-label px-4 pt-3 pb-1">
            Pages
          </div>
          <div
            v-for="(item, index) in filteredNavItems"
            :key="item.path"
            class="command-palette-item"
            :class="{ 'is-selected': selectedIndex === index }"
            @click="selectNavItem(item)"
            @mouseenter="selectedIndex = index"
          >
            <div class="flex items-center gap-3">
              <i :class="[item.icon, 'kb-cp-icon']" />
              <div class="flex-1 min-w-0">
                <div class="font-medium truncate">
                  {{ item.label }}
                </div>
              </div>
              <i class="pi pi-arrow-right text-kb-dim !text-xs" />
            </div>
          </div>
        </div>

        <!-- Members section -->
        <div
          v-if="filteredMembers.length > 0 || showMemberStatus"
          class="mb-2"
        >
          <div class="kb-label px-4 pt-3 pb-1">
            Members
          </div>
          <div
            v-if="showMemberStatus"
            class="px-4 py-2 text-[13px] text-kb-muted flex items-center gap-2"
            role="status"
          >
            <template v-if="membersSearching">
              <KbSpinner />
              Searching members…
            </template>
            <template v-else>
              <i class="pi pi-exclamation-triangle text-kb-gold" />
              Couldn't search members. Try typing again.
            </template>
          </div>
          <div
            v-for="(member, index) in filteredMembers"
            :key="member.id"
            class="command-palette-item"
            :class="{ 'is-selected': selectedIndex === filteredNavItems.length + index }"
            @click="selectMember(member)"
            @mouseenter="selectedIndex = filteredNavItems.length + index"
          >
            <div class="flex items-center gap-3">
              <i class="pi pi-user kb-cp-icon" />
              <div class="flex-1 min-w-0">
                <div class="font-medium truncate">
                  {{ member.name || 'Unnamed' }}
                </div>
                <div class="kb-row-meta truncate">
                  {{ member.email }} · {{ member.subkreweName || 'No SubKrewe' }}
                </div>
              </div>
              <SharedMemberTypeBadge
                v-if="member.member_type"
                :member-type="member.member_type"
                compact
              />
            </div>
          </div>
        </div>

        <!-- SubKrewes section -->
        <div v-if="filteredSubKrewes.length > 0">
          <div class="kb-label px-4 pt-3 pb-1">
            SubKrewes
          </div>
          <div
            v-for="(subkrewe, index) in filteredSubKrewes"
            :key="subkrewe.id"
            class="command-palette-item"
            :class="{ 'is-selected': selectedIndex === filteredNavItems.length + filteredMembers.length + index }"
            @click="selectSubKrewe(subkrewe)"
            @mouseenter="selectedIndex = filteredNavItems.length + filteredMembers.length + index"
          >
            <div class="flex items-center gap-3">
              <i class="pi pi-users kb-cp-icon" />
              <div class="flex-1 min-w-0">
                <div class="font-medium truncate">
                  {{ subkrewe.name }}
                </div>
                <div class="kb-row-meta truncate">
                  {{ subkrewe.captains?.map(c => c.name).join(', ') || 'No captains' }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Initial state -->
      <div
        v-else
        class="p-4 text-center text-[13px] text-kb-muted"
      >
        Type two letters or more to search.
      </div>
    </div>

    <template #footer>
      <div class="flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[11.5px] text-kb-dim">
        <span class="flex items-center gap-1"><kbd class="kb-kbd">↑</kbd><kbd class="kb-kbd">↓</kbd> move</span>
        <span class="flex items-center gap-1"><kbd class="kb-kbd">↵</kbd> open</span>
        <span class="flex items-center gap-1"><kbd class="kb-kbd">{{ modKey }}</kbd><kbd class="kb-kbd">C</kbd> copy name</span>
        <span class="flex items-center gap-1"><kbd class="kb-kbd">{{ shiftKey }}</kbd><kbd class="kb-kbd">{{ modKey }}</kbd><kbd class="kb-kbd">C</kbd> copy email</span>
        <span class="flex items-center gap-1"><kbd class="kb-kbd">esc</kbd> close</span>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { markRaw, nextTick, onMounted, onUnmounted, ref, computed, watch } from 'vue'
import { useToast } from 'primevue/usetoast'
import { AdminMemberDrawerContent, AdminSubKreweDrawerContent } from '#components'
import type { SubKrewe } from '~/shared/types/subkrewe.js'
import type { MemberSearchResult } from '~/server/api/members/search.get.js'
import { SEARCH_MIN_LENGTH, isSearchableQuery } from '~/shared/utils/searchText.js'
import { searchSubKrewes } from '~/utils/subkreweSearch'

const router = useRouter()
const toast = useToast()

// Shared so the search box in the top bar can open the palette too
const isOpen = useState('command-palette-open', () => false)
const searchQuery = ref('')
const selectedIndex = ref(0)
const searchInputRef = ref<{ $el: HTMLInputElement } | null>(null)
const isLoading = ref(false)

// Detect Mac vs Windows for keyboard shortcuts
const isMac = computed(() => {
  if (typeof navigator === 'undefined') return true
  return navigator.platform?.toUpperCase().includes('MAC') ?? navigator.userAgent?.toUpperCase().includes('MAC') ?? true
})
const modKey = computed(() => isMac.value ? '⌘' : 'Ctrl')
const shiftKey = computed(() => isMac.value ? '⬆' : 'Shift')

const membersStore = useMembersStore()
const subkrewesStore = useSubKrewesStore()
const { openDrawer } = useDrawer()

// Navigation items (pages)
interface NavItem {
  label: string
  path: string
  icon: string
  keywords?: string[] // Additional search terms
}

const navigationItems: NavItem[] = [
  { label: 'Home', path: '/', icon: 'pi pi-home', keywords: ['dashboard'] },
  { label: 'My SubKrewe', path: '/subkrewe', icon: 'pi pi-users', keywords: ['find', 'join'] },
  { label: 'My QR Code', path: '/checkin/qrcode', icon: 'pi pi-qrcode', keywords: ['checkin', 'wristband'] },
  { label: 'Settings', path: '/settings', icon: 'pi pi-cog', keywords: ['profile', 'account'] },
  { label: 'Support', path: '/support', icon: 'pi pi-question-circle', keywords: ['help', 'faq'] },
  // Admin pages
  { label: 'Members Admin', path: '/admin/members', icon: 'pi pi-users', keywords: ['manage', 'admin'] },
  { label: 'SubKrewes Admin', path: '/admin/subkrewes', icon: 'pi pi-sitemap', keywords: ['manage', 'admin'] },
  { label: 'Emails', path: '/admin/emails', icon: 'pi pi-envelope', keywords: ['reporting', 'mailchimp'] },
  { label: 'Inventory', path: '/admin/inventory', icon: 'pi pi-box', keywords: ['equipment', 'manage'] },
  { label: 'Products & Pricing', path: '/admin/products', icon: 'pi pi-shopping-cart', keywords: ['stripe', 'membership', 'dues'] },
  { label: 'Transactions', path: '/admin/transactions', icon: 'pi pi-credit-card', keywords: ['stripe', 'payment', 'lookup', 'link', 'transfer'] },
  { label: 'Equipment Station', path: '/checkin/equipment', icon: 'pi pi-box', keywords: ['inventory'] },
  { label: 'Lineup Manage', path: '/admin/lineup', icon: 'pi pi-map-marker', keywords: ['parade', 'order'] },
  { label: 'Lineup View', path: '/lineup', icon: 'pi pi-eye', keywords: ['parade', 'order'] },
  { label: 'Lineup Check In', path: '/admin/lineup-status', icon: 'pi pi-check-circle', keywords: ['parade'] },
  { label: 'Radio List', path: '/admin/radio-list', icon: 'pi pi-megaphone', keywords: ['lineup', 'captains'] },
  { label: 'Lifetimers', path: '/admin/reporting/lifetimers', icon: 'pi pi-star', keywords: ['reporting'] },
  { label: 'VIP Passes', path: '/admin/reporting/vip-passes', icon: 'pi pi-ticket', keywords: ['reporting'] },
  { label: 'Checked In Report', path: '/admin/reporting/checked-in', icon: 'pi pi-check-circle', keywords: ['reporting'] },
  { label: 'Tracking', path: '/tracking', icon: 'pi pi-map', keywords: ['location', 'gps'] },
  { label: 'Check People In', path: '/checkin', icon: 'pi pi-check-circle', keywords: ['scan', 'wristband'] },
]

// Filtered navigation items
const filteredNavItems = computed(() => {
  if (searchQuery.value.length < 2) return []

  const query = searchQuery.value.toLowerCase()
  return navigationItems.filter((item) => {
    const labelMatch = item.label.toLowerCase().includes(query)
    const keywordMatch = item.keywords?.some(k => k.toLowerCase().includes(query))
    return labelMatch || keywordMatch
  }).slice(0, 5)
})

// Members come from /api/members/search rather than the store: the palette is
// mounted in the default layout for every admin, and the roster preload only
// runs on /admin/* routes — so opening it anywhere else used to download the
// whole members collection. The endpoint also applies redshirt-admin scoping
// server-side, which the client could only fake after loading everyone.
const {
  results: filteredMembers,
  pending: membersSearching,
  failed: memberSearchFailed,
  search: searchMembers,
  reset: resetMemberSearch,
} = useDebouncedMemberSearch({ scope: 'admin' })

watch(searchQuery, query => searchMembers(query))

const isSearchable = computed(() => isSearchableQuery(searchQuery.value, SEARCH_MIN_LENGTH))

// The member search can still be running, or have failed, when pages and
// SubKrewes have nothing: only call it "no results" once it has finished.
const showMemberStatus = computed(() =>
  isSearchable.value && (membersSearching.value || memberSearchFailed.value),
)

const filteredSubKrewes = computed(() =>
  searchSubKrewes(subkrewesStore.subkrewesArray, searchQuery.value, { limit: 5 }),
)

const hasResults = computed(() => {
  return filteredNavItems.value.length > 0 || filteredMembers.value.length > 0 || filteredSubKrewes.value.length > 0
})

const totalResults = computed(() => {
  return filteredNavItems.value.length + filteredMembers.value.length + filteredSubKrewes.value.length
})

// Reset selected index when results change
watch([filteredNavItems, filteredMembers, filteredSubKrewes], () => {
  selectedIndex.value = 0
})

// Scroll selected item into view
watch(selectedIndex, () => {
  nextTick(() => {
    const selected = document.querySelector('.command-palette-item.is-selected')
    selected?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  })
})

// Select member and open in drawer
const selectMember = async (member: MemberSearchResult) => {
  isOpen.value = false

  // Search results are a lightweight projection, so the drawer needs the real
  // document: served from the store when it is there, otherwise one doc read.
  const canonicalMember = await membersStore.fetchMember(member.id)
  if (!canonicalMember) {
    toast.add({
      severity: 'warn',
      summary: 'Member not found',
      detail: `${member.name || member.email} could not be loaded.`,
      life: 3000,
    })
    return
  }

  openDrawer({
    title: canonicalMember.name || 'Member Details',
    data: canonicalMember,
    component: markRaw(AdminMemberDrawerContent),
  })
}

// Select SubKrewe and open in drawer
const selectSubKrewe = (subkrewe: SubKrewe) => {
  isOpen.value = false

  // Get canonical subkrewe from store
  const subkrewePath = `subkrewes/${subkrewe.id}`
  const canonicalSubkrewe = subkrewesStore.subkrewes[subkrewePath] || subkrewe

  openDrawer({
    title: canonicalSubkrewe.name || 'SubKrewe Details',
    data: canonicalSubkrewe,
    component: markRaw(AdminSubKreweDrawerContent),
  })
}

// Select navigation item and navigate
const selectNavItem = (item: NavItem) => {
  isOpen.value = false
  router.push(item.path)
}

// Keyboard navigation
const onKeyDown = (event: KeyboardEvent) => {
  // Handle Cmd/Ctrl+C to copy name, Cmd/Ctrl+Shift+C to copy email
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'c') {
    const selected = getSelectedItem()
    if (selected) {
      event.preventDefault()
      const textToCopy = event.shiftKey
        ? ('email' in selected ? selected.email : '')
        : ('name' in selected ? selected.name : '')
      if (textToCopy) {
        navigator.clipboard.writeText(textToCopy)
      }
    }
    return
  }

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      if (selectedIndex.value < totalResults.value - 1) {
        selectedIndex.value++
      }
      break
    case 'ArrowUp':
      event.preventDefault()
      if (selectedIndex.value > 0) {
        selectedIndex.value--
      }
      break
    case 'Enter':
      event.preventDefault()
      selectCurrentItem()
      break
    case 'Escape':
      event.preventDefault()
      isOpen.value = false
      break
  }
}

// Get currently selected item (member, subkrewe, or nav item)
const getSelectedItem = (): MemberSearchResult | SubKrewe | NavItem | null => {
  const navCount = filteredNavItems.value.length
  const memberCount = filteredMembers.value.length

  if (selectedIndex.value < navCount) {
    return filteredNavItems.value[selectedIndex.value] || null
  }
  else if (selectedIndex.value < navCount + memberCount) {
    return filteredMembers.value[selectedIndex.value - navCount] || null
  }
  else {
    const subkreweIndex = selectedIndex.value - navCount - memberCount
    return filteredSubKrewes.value[subkreweIndex] || null
  }
}

// Select currently highlighted item
const selectCurrentItem = () => {
  const navCount = filteredNavItems.value.length
  const memberCount = filteredMembers.value.length

  if (selectedIndex.value < navCount) {
    const item = filteredNavItems.value[selectedIndex.value]
    if (item) selectNavItem(item)
  }
  else if (selectedIndex.value < navCount + memberCount) {
    const member = filteredMembers.value[selectedIndex.value - navCount]
    if (member) void selectMember(member)
  }
  else {
    const subkreweIndex = selectedIndex.value - navCount - memberCount
    const subkrewe = filteredSubKrewes.value[subkreweIndex]
    if (subkrewe) selectSubKrewe(subkrewe)
  }
}

// Close handler
const onClose = () => {
  searchQuery.value = ''
  selectedIndex.value = 0
  // Cleared here rather than waiting for the debounced watcher, so a reopened
  // palette never shows the previous query's results.
  resetMemberSearch()
}

// Fetch data when palette opens (if not already loaded). Members are not
// fetched here — they are searched server-side. SubKrewes are bounded and
// filtered locally, so the collection is still worth loading once.
watch(isOpen, async (open) => {
  if (open) {
    nextTick(() => {
      const input = searchInputRef.value?.$el?.querySelector('input') || searchInputRef.value?.$el
      input?.focus()
    })
    if (!subkrewesStore.initialized) {
      isLoading.value = true
      await subkrewesStore.fetchSubKrewes()
      isLoading.value = false
    }
  }
})

// Global keyboard shortcut handler
const handleGlobalKeyDown = (event: KeyboardEvent) => {
  // Cmd+K (Mac) or Ctrl+K (Windows/Linux) - toggle palette
  if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
    event.preventDefault()
    isOpen.value = !isOpen.value

    // Focus the input after dialog opens
    if (isOpen.value) {
      nextTick(() => {
        const input = searchInputRef.value?.$el?.querySelector('input') || searchInputRef.value?.$el
        input?.focus()
      })
    }
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleGlobalKeyDown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleGlobalKeyDown)
})
</script>

<style scoped>
.command-palette-results {
  max-height: min(400px, 60vh);
  overflow-y: auto;
  padding-bottom: 0.375rem;
}

/* The row the arrow keys are on: the accent wash and a rule on the left */
.command-palette-item {
  padding: 0.5rem 1rem;
  border-left: 2px solid transparent;
  cursor: pointer;
}

.command-palette-item:hover {
  background: var(--kb-hover);
}

.command-palette-item.is-selected {
  border-left-color: var(--kb-accent);
  background: var(--kb-accent-wash);
}

.kb-cp-icon {
  font-size: 0.8125rem;
  color: var(--kb-dim);
}

.command-palette-item.is-selected .kb-cp-icon {
  color: var(--kb-accent);
}
</style>

<style>
/* The dialog itself (teleported, so not scoped) */
.command-palette-dialog {
  width: calc(100vw - 1.5rem);
  max-width: 700px;
}

.command-palette-dialog .p-dialog-header {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--kb-line);
}

.command-palette-dialog .p-dialog-content {
  padding: 0;
}

.command-palette-dialog .p-dialog-footer {
  padding: 0.625rem 1rem;
  border-top: 1px solid var(--kb-line);
}

.command-palette-input {
  border: none !important;
  box-shadow: none !important;
  font-size: 1rem;
}

.command-palette-input:focus {
  outline: none !important;
  box-shadow: none !important;
}

.command-palette-mask {
  backdrop-filter: blur(4px);
}
</style>
